import React, { useEffect, useState } from "react";
import { Spin } from "antd";
import "bootstrap/dist/css/bootstrap.css";
import SidebarNav from "../sidebar";
import { Link } from "react-router-dom";
import { getThirdPartyStats, getServiceStatus } from "../../../service/api";
import { toast } from "react-toastify";

const StatusBadge = ({ status }) => {
  const colorMap = {
    operational: "badge-success",
    degraded: "badge-warning",
    auth_error: "badge-warning",
    not_configured: "badge-secondary",
    down: "badge-danger",
  };
  const labelMap = {
    operational: "Operational",
    degraded: "Degraded",
    auth_error: "Auth Error",
    not_configured: "Not Configured",
    down: "Down",
  };
  return (
    <span className={`badge ${colorMap[status] || "badge-secondary"}`}>
      {labelMap[status] || status || "Unknown"}
    </span>
  );
};

const ThirdPartyStats = () => {
  const [stats, setStats] = useState(null);
  const [serviceStatus, setServiceStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [statusLoading, setStatusLoading] = useState(false);

  const fetchStats = () => {
    setLoading(true);
    getThirdPartyStats()
      .then((res) => {
        if (res?.status) {
          setStats(res?.data);
        }
        setLoading(false);
      })
      .catch((e) => {
        toast.error(e?.response?.data?.message || "Failed to fetch stats", { toastId: 'third-party-stats-fetch-error' });
        setLoading(false);
      });
  };

  const fetchServiceStatus = () => {
    try {
      setStatusLoading(true);
      getServiceStatus()
        .then((res) => {
          if (res?.status) {
            setServiceStatus(res?.data);
          }
          setStatusLoading(false);
        })
        .catch((e) => {
          toast.error(
            e?.response?.data?.message || "Failed to fetch service status", { toastId: 'third-party-stats-fetch-error' }
          );
          setStatusLoading(false);
        });
    } catch {
      setStatusLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
    fetchServiceStatus();
  }, []);

  const serviceNames = {
    database: "Database",
    digio: "Digio",
    signzy: "Signzy",
    complyAdvantage: "ComplyAdvantage",
    notabene: "Notabene",
    trm: "TRM Labs",
  };

  return (
    <>
      <SidebarNav />
      <div className="page-wrapper">
        <div className="content container-fluid">
          {/* Page Header */}
          <div className="page-header">
            <div className="row">
              <div className="col-sm-7 col-auto">
                <h3 className="page-title">Third Party Stats</h3>
                <ul className="breadcrumb">
                  <li className="breadcrumb-item">
                    <Link to="/admin">Dashboard</Link>
                  </li>
                  <li className="breadcrumb-item active">Third Party Stats</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Service Status Section */}
          <div className="row mb-4">
            <div className="col-sm-12">
              <div className="card">
                <div className="card-header d-flex justify-content-between align-items-center">
                  <h4 className="card-title mb-0">Service Status</h4>
                  <button
                    className="btn btn-sm btn-outline-primary"
                    onClick={fetchServiceStatus}
                    disabled={statusLoading}
                  >
                    {statusLoading ? "Checking..." : "Refresh"}
                  </button>
                </div>
                <div className="card-body">
                  {statusLoading && !serviceStatus ? (
                    <div className="text-center py-4">
                      <Spin size="large" />
                    </div>
                  ) : serviceStatus ? (
                    <div className="row">
                      {Object.entries(serviceStatus).map(([key, value]) => (
                        <div key={key} className="col-xl-3 col-sm-4 col-6 mb-3">
                          <div
                            className="card mb-0 h-100"
                            style={{ border: "1px solid #e3e3e3" }}
                          >
                            <div className="card-body p-3 text-center">
                              <h6 className="mb-2">{serviceNames[key] || key}</h6>
                              <StatusBadge status={value?.status} />
                              {value?.message && (
                                <p
                                  className="text-muted mb-0 mt-1"
                                  style={{ fontSize: "12px" }}
                                >
                                  {value.message}
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-muted mb-0">No data available</p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Stats Section */}
          {loading && !stats ? (
            <div className="text-center py-5">
              <Spin size="large" />
            </div>
          ) : stats ? (
            <>
              {/* Database Stats */}
              <div className="row mb-4">
                <div className="col-sm-12">
                  <h5 className="mb-3">Database Users</h5>
                </div>
                <div className="col-xl-2 col-sm-4 col-6 mb-3">
                  <div className="card mb-0 h-100">
                    <div className="card-body text-center">
                      <h3 className="mb-1">{stats?.database?.totalUsers ?? 0}</h3>
                      <p className="text-muted mb-0">Total Users</p>
                    </div>
                  </div>
                </div>
                <div className="col-xl-2 col-sm-4 col-6 mb-3">
                  <div className="card mb-0 h-100">
                    <div className="card-body text-center">
                      <h3 className="mb-1 text-success">
                        {stats?.database?.kycApproved ?? 0}
                      </h3>
                      <p className="text-muted mb-0">KYC Approved</p>
                    </div>
                  </div>
                </div>
                <div className="col-xl-2 col-sm-4 col-6 mb-3">
                  <div className="card mb-0 h-100">
                    <div className="card-body text-center">
                      <h3 className="mb-1 text-warning">
                        {stats?.database?.kycPending ?? 0}
                      </h3>
                      <p className="text-muted mb-0">KYC Pending</p>
                    </div>
                  </div>
                </div>
                <div className="col-xl-2 col-sm-4 col-6 mb-3">
                  <div className="card mb-0 h-100">
                    <div className="card-body text-center">
                      <h3 className="mb-1 text-danger">
                        {stats?.database?.kycRejected ?? 0}
                      </h3>
                      <p className="text-muted mb-0">KYC Rejected</p>
                    </div>
                  </div>
                </div>
                <div className="col-xl-2 col-sm-4 col-6 mb-3">
                  <div className="card mb-0 h-100">
                    <div className="card-body text-center">
                      <h3 className="mb-1 text-info">
                        {stats?.database?.kycInProcess ?? 0}
                      </h3>
                      <p className="text-muted mb-0">KYC In Process</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Third Party Service Counts */}
              <div className="row mb-4">
                <div className="col-sm-12">
                  <h5 className="mb-3">Third Party Service Counts</h5>
                </div>
                <div className="col-xl-2 col-sm-4 col-6 mb-3">
                  <div className="card mb-0 h-100">
                    <div className="card-body text-center">
                      <h3 className="mb-1">{stats?.digio?.totalUsers ?? 0}</h3>
                      <p className="text-muted mb-0">Digio</p>
                      {stats?.digio?.error && (
                        <small className="text-danger">{stats.digio.error}</small>
                      )}
                    </div>
                  </div>
                </div>
                <div className="col-xl-2 col-sm-4 col-6 mb-3">
                  <div className="card mb-0 h-100">
                    <div className="card-body text-center">
                      <h3 className="mb-1">{stats?.signzy?.totalUsers ?? 0}</h3>
                      <p className="text-muted mb-0">Signzy</p>
                      {stats?.signzy?.error && (
                        <small className="text-danger">
                          {stats.signzy.error}
                        </small>
                      )}
                    </div>
                  </div>
                </div>
                <div className="col-xl-2 col-sm-4 col-6 mb-3">
                  <div className="card mb-0 h-100">
                    <div className="card-body text-center">
                      <h3 className="mb-1">
                        {stats?.complyAdvantage?.totalUsers ?? 0}
                      </h3>
                      <p className="text-muted mb-0">ComplyAdvantage</p>
                      {stats?.complyAdvantage?.error && (
                        <small className="text-danger">
                          {stats.complyAdvantage.error}
                        </small>
                      )}
                    </div>
                  </div>
                </div>
                <div className="col-xl-2 col-sm-4 col-6 mb-3">
                  <div className="card mb-0 h-100">
                    <div className="card-body text-center">
                      <h3 className="mb-1">
                        {stats?.notabene?.totalUsers ?? 0}
                      </h3>
                      <p className="text-muted mb-0">Notabene</p>
                      {stats?.notabene?.error && (
                        <small className="text-danger">
                          {stats.notabene.error}
                        </small>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* TRM Stats */}
              <div className="row mb-4">
                <div className="col-sm-12">
                  <h5 className="mb-3">TRM Labs</h5>
                </div>
                <div className="col-xl-3 col-sm-4 col-6 mb-3">
                  <div className="card mb-0 h-100">
                    <div className="card-body text-center">
                      <h3 className="mb-1">{stats?.trm?.openAlerts ?? 0}</h3>
                      <p className="text-muted mb-0">Open Alerts</p>
                    </div>
                  </div>
                </div>
                {stats?.trm?.transfersCountByStatus &&
                  typeof stats.trm.transfersCountByStatus === "object" &&
                  Object.entries(stats.trm.transfersCountByStatus).map(
                    ([statusKey, count]) => (
                      <div
                        key={statusKey}
                        className="col-xl-3 col-sm-4 col-6 mb-3"
                      >
                        <div className="card mb-0 h-100">
                          <div className="card-body text-center">
                            <h3 className="mb-1">{count ?? 0}</h3>
                            <p className="text-muted mb-0 text-capitalize">
                              {statusKey.replace(/_/g, " ")}
                            </p>
                          </div>
                        </div>
                      </div>
                    )
                  )}
                {stats?.trm?.error && (
                  <div className="col-12">
                    <small className="text-danger">{stats.trm.error}</small>
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="text-center py-5">
              <p className="text-muted">No stats available</p>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default ThirdPartyStats;
